import { gql } from "@apollo/client";
import * as MENUS from "constants/menus";
import { Layout, Loading } from "features"; // Blocks eventually
import {
  NavigationMenu,
  GalleryCarousel,
  PostContent,
  VideoGallery,
} from "components";
import { Button } from "components/Button";
import { PetDetails } from "components/PetDetails";
import { PetAttributes } from "components/PetAttributes";
import { SplitText } from "components/SplitText";
import { Accordion } from "components/Accordion";

import {
  BLOG_INFO_FRAGMENT,
  SITE_SETTINGS_FRAGMENT,
  SEO_FRAGMENT,
  SEO_CONFIG_FRAGMENT,
  MEDIA_ITEM_FRAGMENT,
} from "fragments";
import Paws from "public/text-image-assets/paws.svg";
import Back from "public/icons/back-icon.svg";
import getAgeString from "utilities/getAgeString";

const headingIcon = {
  sourceUrl: `${process.env.NEXT_PUBLIC_WORDPRESS_URL}/wp-content/uploads/2023/01/faq.svg`,
  altText: "FAQ Icon",
  caption: null,
  description: null,
  srcSet: null,
  sizes: "(max-width: 300px) 100vw, 300px",
  id: "cG9zdDoxMTgy",
  mediaDetails: {
    width: 120,
    height: 120,
  },
  mimeType: "image/svg+xml",
};

export default function Component(props) {
  const { data, loading, error } = props;

  if (loading) {
    return <Loading type="page" />;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  const {
    animal,
    headerMenuItems,
    footerMenuItems,
    siteSettings,
    seo: defaultSEO,
  } = data;

  const { social } = defaultSEO;

  const {
    seo,
    animalDetails,
    sidebarOptions,
    petAttributes,
    primaryBreeds,
    secondaryBreeds,
    weightGroups,
    ageGroups,
    virtualFosterables,
    relatedAnimals,
    featuredImage,
  } = animal;

  const isVirtuallyFosterable = virtualFosterables?.nodes.length > 0;

  const {
    animalNid: nid,
    animalName: name,
    animalBirthday: birthday,
    animalSex: sex,
    animalWeight: weight,
    animalUniqueId: uniqueId,
    animalPhotoGallery,
    animalVideoGallery,
    animalDescription: description,
    animalCoverPhoto: image,
  } = animalDetails;

  const sourceUrl = image;

  const gallery = animalPhotoGallery?.map((photo, index) => {
    return {
      src: photo.photo,
      alt: `${name} Gallery Photo ${index + 1}`,
    };
  });

  const { useSidebar, dogsWidget, donateWidget } =
    sidebarOptions.sidebarOptions;

  const {
    address,
    customAddressLabel,
    phoneNumber,
    logo,
    logoWhite,
    logoAlt,
    cta,
    email,
    turnOnAnnouncements,
    announcements,
    faqTitle,
    faqItems,
    adoptionRequirements,
    adoptionFees,
  } = siteSettings.siteSettings;
  const splitTextBlock = {
    hasPawIcon: false,
    pawIconPosition: "top-right",
    title: null,
    variant: "basic",
    cardRepeater: [
      {
        content: adoptionRequirements,
        link: null,
        title: "ADOPTION REQUIREMENTS",
        linkType: "primary-outline",
      },
      {
        content: adoptionFees,
        link: null,
        title: "ADOPTION  FEES",
        linkType: "primary-outline",
      },
    ],
  };

  return (
    <Layout
      headerMenuItems={headerMenuItems}
      footerMenuItems={footerMenuItems}
      siteSettings={siteSettings}
      seo={seo}
      logo={logo}
      logoWhite={logoWhite}
      logoAlt={logoAlt}
      cta={cta}
      twitterUser={defaultSEO.social.twitter.username}
      address={address}
      customAddressLabel={customAddressLabel}
      phoneNumber={phoneNumber}
      email={email}
      social={social}
      turnOnAnnouncements={turnOnAnnouncements}
      announcements={announcements}
      useSidebar={useSidebar ?? true}
      dogsWidget={dogsWidget ?? false}
      donateWidget={donateWidget ?? true}
      topBorder={true}
      relatedAnimals={relatedAnimals?.nodes}
      isAnimalSingle
      adoptionLink={`https://www.shelterluv.com/matchme/adopt/GDR-A-${uniqueId}`}
      animalName={name}
      animalId={nid}
      seoImage={sourceUrl}
      isVirtuallyFosterable={isVirtuallyFosterable}
    >
      <div className="container relative mx-auto">
        <button type="button" className="flex flex-row gap-2 font-body" onClick={() => window.close()}>
          <Back /> Back to Search
        </button>
        <div className="flex flex-col text-center">
          <h1 className="text-center font-heading text-6xl font-bold">
            {name}
          </h1>
          <p className="text-2xl">#GDR-A-{uniqueId}</p>
        </div>
        <div className={`flex flex-col lg:px-20`}>
          {gallery && <GalleryCarousel images={gallery} />}
          <VideoGallery videos={animalVideoGallery} />
          <PetDetails
            name={name}
            primaryBreed={primaryBreeds?.nodes[0]?.name}
            secondaryBreed={secondaryBreeds?.nodes[0]?.name}
            sex={sex}
            weight={weightGroups?.nodes[0]?.name}
            age={
              birthday
                ? getAgeString(parseInt(birthday))
                : ageGroups?.nodes[0]?.name
            }
          />
          {description && <PostContent content={description} />}
          <PetAttributes attributes={petAttributes} />
          <div className="relative my-10 flex flex-col text-center">
            <div
              className={`absolute -top-12 right-0 hidden -scale-x-100 md:flex`}
            >
              <Paws />
            </div>
            <h2 className="z-10 mx-auto max-w-xl text-center font-heading text-6xl font-bold capitalize">
              Ready to meet your new best friend?
            </h2>
            <p className="my-10 text-xl">
              <Button
                href={`https://www.shelterluv.com/matchme/adopt/GDR-A-${uniqueId}`}
                target="_blank"
                variant="primary"
                className="px-8 py-4"
              >
                Apply For Adoption
              </Button>
            </p>
            {isVirtuallyFosterable && (
              <Button
                variant="secondary-outline"
                className={`mx-auto w-fit text-center lg:px-4`}
                href={`/virtual-foster?animalId=${nid}&animalName=${name}`}
                target="_self"
              >
                Become Virtual Foster
              </Button>
            )}
          </div>
          <SplitText {...splitTextBlock} />
          <Accordion
            title={faqTitle}
            itemRepeater={faqItems}
            hasHeadingIcon={true}
            headingIcon={headingIcon}
          />
        </div>
      </div>
    </Layout>
  );
}

Component.query = gql`
  query PostPage(
    $databaseId: ID!
    $headerLocation: MenuLocationEnum!
    $footerLocation: MenuLocationEnum!
    $asPreview: Boolean = false
  ) {
    generalSettings {
      ...BlogInfoFragment
    }
    siteSettings {
      ...SiteSettingsFragment
    }
    seo {
      ...SEOConfigFragment
    }
    animal(id: $databaseId, idType: DATABASE_ID, asPreview: $asPreview) {
      id
      title
      content
      date
      featuredImage {
        node {
          ...MediaItemFragment
        }
      }
      animalDetails {
        animalBirthday
        animalIntakeDate
        animalDescription
        animalName
        animalNid
        animalSex
        animalUniqueId
        animalWeight
        animalPhotoGallery {
          photo
        }
        animalVideoGallery {
          videoId
          embedUrl
          youtubeUrl
          thumbUrl
        }
        animalCoverPhoto
      }
      petAttributes {
        nodes {
          id
          description
          name
          slug
          parent {
            node {
              id
              name
              slug
            }
          }
        }
      }
      primaryBreeds {
        nodes {
          id
          description
          name
          slug
          parent {
            node {
              id
              name
              slug
            }
          }
        }
      }
      secondaryBreeds {
        nodes {
          id
          description
          name
          slug
          parent {
            node {
              id
              name
              slug
            }
          }
        }
      }
      ageGroups {
        nodes {
          id
          description
          name
          slug
          parent {
            node {
              id
              name
              slug
            }
          }
        }
      }
      virtualFosterables {
        nodes {
          id
          description
          name
          slug
          parent {
            node {
              id
              name
              slug
            }
          }
        }
      }
      weightGroups {
        nodes {
          id
          description
          name
          slug
          parent {
            node {
              id
              name
              slug
            }
          }
        }
      }
      relatedAnimals {
        nodes {
          id
          uri
          content
          animalDetails {
            animalBirthday
            animalName
            animalNid
            animalSex
            animalUniqueId
            animalWeight
            animalPhotoGallery {
              photo
            }
            animalCoverPhoto
          }
          featuredImage {
            node {
              ...MediaItemFragment
            }
          }
          primaryBreeds {
            nodes {
              id
              description
              name
              slug
            }
          }
          secondaryBreeds {
            nodes {
              id
              description
              name
              slug
            }
          }
          ageGroups {
            nodes {
              id
              description
              name
              slug
            }
          }
          weightGroups {
            nodes {
              id
              description
              name
              slug
            }
          }
          petAttributes {
            nodes {
              id
              description
              name
              slug
            }
          }
        }
      }
      seo {
        ...SEOFragment
      }
      sidebarOptions {
        sidebarOptions {
          dogsWidget
          useSidebar
          donateWidget
        }
      }
    }
    headerMenuItems: menuItems(
      where: { location: $headerLocation }
      first: 50
    ) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
    footerMenuItems: menuItems(
      where: { location: $footerLocation }
      first: 50
    ) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
  }
  ${BLOG_INFO_FRAGMENT}
  ${SITE_SETTINGS_FRAGMENT}
  ${NavigationMenu.fragments.entry}
  ${SEO_FRAGMENT}
  ${SEO_CONFIG_FRAGMENT}
  ${MEDIA_ITEM_FRAGMENT}
`;

Component.variables = ({ databaseId }, ctx) => {
  return {
    databaseId,
    headerLocation: MENUS.PRIMARY_LOCATION,
    footerLocation: MENUS.FOOTER_LOCATION,
    asPreview: ctx?.asPreview,
  };
};
